import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SearchableSelect.css';

function useOnClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

/**
 * Single-select combobox aligned with MultiSelectDropdown styling.
 * @param {string} name - passed to onChange synthetic event target
 * @param {string} value - selected option value (string)
 * @param {Array<{value: string|number, label: string}>} options
 * @param {function(React.SyntheticEvent): void} onChange - like native select (e.target.name, e.target.value)
 * @param {string} placeholder - empty state label on trigger
 * @param {boolean} [disabled]
 * @param {string} [searchPlaceholder]
 */
function SearchableSelect({
  name,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  searchPlaceholder = 'Search site…',
  emptyOptionLabel = 'Select Site',
}) {
  const [open, setOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  useOnClickOutside(ref, () => {
    setOpen(false);
    setFilterText('');
  });

  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  const norm = (v) => (v == null ? '' : String(v));
  const selectedLabel = useMemo(() => {
    const hit = options.find((o) => norm(o.value) === norm(value));
    return hit ? hit.label : '';
  }, [options, value]);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const lab = String(o.label || '').toLowerCase();
      return lab.includes(q) || norm(o.value).toLowerCase().includes(q);
    });
  }, [options, filterText]);

  const emit = (nextValue) => {
    onChange({ target: { name, value: nextValue === '' || nextValue == null ? '' : String(nextValue) } });
    setOpen(false);
    setFilterText('');
  };

  return (
    <div className="searchable-select" ref={ref}>
      <button
        type="button"
        className="searchable-select-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        title={selectedLabel || placeholder}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={`searchable-select-summary${!value ? ' searchable-select-summary--placeholder' : ''}`}>
          {value ? selectedLabel : placeholder}
        </span>
        <span className="searchable-select-arrow">▼</span>
      </button>
      {open && (
        <div className="searchable-select-panel" role="listbox">
          <div className="searchable-select-search-wrap">
            <input
              ref={searchInputRef}
              type="search"
              className="searchable-select-search"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={searchPlaceholder}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              aria-label={searchPlaceholder}
            />
          </div>
          <div className="searchable-select-list">
            <button
              type="button"
              className={`searchable-select-option${!value ? ' searchable-select-option--active' : ''}`}
              onClick={() => emit('')}
            >
              {emptyOptionLabel}
            </button>
            {filtered.length === 0 ? (
              <div className="searchable-select-empty">No matches</div>
            ) : (
              filtered.map((opt) => {
                const active = norm(opt.value) === norm(value);
                return (
                  <button
                    key={norm(opt.value)}
                    type="button"
                    className={`searchable-select-option${active ? ' searchable-select-option--active' : ''}`}
                    onClick={() => emit(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
